import React, { useEffect, useState } from "react";
import _ from "lodash";
import styled, { css } from "styled-components";

import theme from "../../styles/theme";
import { Link } from "react-router-dom";
import { useScheduleContext } from "../../contexts/schedule";
import { buildRowsFromStandings, buildRowsFromTeamStandings } from "../../utils/helpers/standings";
import { isDark } from "../../utils/helpers/colors";
import { getFlag } from "../../utils/helpers/flags";

const getOppositeColor = (color, defaultColor) => {
  if (!color) color = defaultColor;
  return isDark(color) ? theme.colors.white : theme.colors.darkLight;
};

/**
 * The row grid is built here rather than hard-coded, because which columns exist depends on the
 * mode (teams have no team column) and on the season (no gross-points column unless it drops
 * rounds). The header row is laid out on the same track, so the two cannot drift apart.
 */
const RACE_CELL = 28;
const RACE_GAP = 6;
const COL_GAP = 16;
const ROW_PADDING = 24;

/**
 * Total width the row must reserve. Every row is its own grid, so leaving any track content-sized
 * lets a long name in one row shift its columns out of step with the header's. Reserving an
 * explicit width keeps all rows on identical tracks and ellipsises overlong text instead.
 */
const rowWidth = ({ team, gross, nameCol, roundCount }) => {
  const widths = [24, nameCol, team ? 240 : 0, gross ? 50 : 0, racesWidth(roundCount), 50].filter(Boolean);
  const gaps = COL_GAP * (widths.length - 1);
  return widths.reduce((sum, w) => sum + w, 0) + gaps + ROW_PADDING;
};

const racesWidth = (roundCount) =>
  roundCount * RACE_CELL + Math.max(0, roundCount - 1) * RACE_GAP;

const gridTemplate = ({ team, gross, races, nameCol }) =>
  ["24px", `minmax(${nameCol}px, 1fr)`, team && "240px", gross && "50px", races && "auto", "50px"]
    .filter(Boolean)
    .join(" ");

const Root = styled.div`
  padding: 4px 0px;
  font-family: "Montserrat", sans-serif;
`;

const Table = styled.div`
  margin-left: auto;
  margin-right: auto;

  ${(props) =>
    props.$scrollable &&
    css`
      overflow-x: auto;
      /* the table scrolls sideways, the page body must not */
      max-width: 100%;
    `}
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const ColPosition = styled.div`
  font-weight: 600;
  opacity: 0.7;
`;

const DriverName = styled.div`
  /* min-width:0 so a long name cannot widen this row's track past the shared floor set in
     gridTemplate -- each row is its own grid, so a content-sized column desynchronises them. */
  min-width: 0;
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 15px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const TeamNameCell = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-weight: 600;
  font-size: 15px;
  overflow: hidden;

  & > * {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

const TeamRoster = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.grey};
  white-space: pre-wrap;
`;

const Row = styled.div`
  display: grid;
  gap: 16px;
  align-items: center;
  padding: 12px 12px;

  background: linear-gradient(
      to left,
      ${theme.colors.dark} 70%,
      ${(props) => props.color || theme.colors.white} 100%
    )
    right;
  background-size: 200% 150%;
  background-position: right;

  &:nth-of-type(2n + 1) {
    background: linear-gradient(
        to left,
        ${theme.colors.darkLight} 70%,
        ${(props) => props.color || theme.colors.white} 100%
      )
      right;
    background-size: 200% 150%;
    background-position: right;
  }

  transition: all 0.3s ease-in-out;
  user-select: none;

  grid-template-columns: ${(props) => props.$colsFull};
  ${(props) => props.$scrollable && css`min-width: ${props.$minWidth}px;`}

  ${(props) =>
    !props.$scrollable &&
    css`
      @media screen and (max-width: ${theme.breakpointsCustom.lg}px) {
        grid-template-columns: ${props.$colsLg};
      }

      @media screen and (max-width: ${theme.breakpointsCustom.md}px) {
        grid-template-columns: ${props.$colsMd};
      }
    `}

  &:hover {
    background-position: left;

    & ${ColPosition} {
      color: ${getOppositeColor(theme.colors.white)};
      transition: all 0.3s ease-in-out;
    }

    & ${DriverName} {
      color: ${(props) => getOppositeColor(props.color, theme.colors.white)};
      transition: all 0.3s ease-in-out;
    }
  }
`;

const HeaderRow = styled(Row)`
  background: transparent;
  &:nth-of-type(2n + 1) {
    background: transparent;
  }
  &:hover {
    background-position: right;

    /* The header is not an interactive row, so it must not pick up Row's hover styling. */
    & ${ColPosition} {
      color: ${theme.colors.grey};
    }
  }
  padding-bottom: 8px;
  border-bottom: 1px solid ${theme.colors.darkLight};
  cursor: default;

  /* Doubled class so this beats the per-column font sizes: every header label is one
     type style regardless of which column it sits above. */
  && > * {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    line-height: 1;
    text-transform: uppercase;
    color: ${theme.colors.grey};
    opacity: 1;
  }
`;

const DriverLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  display: inline-block;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  &:hover {
    text-decoration: none;
  }
`;

const RaceLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover {
    text-decoration: none;
    background-color: rgba(0, 0, 0, 0.12);
  }
`;

const hideBelow = (breakpoint) => css`
  @media screen and (max-width: ${breakpoint}px) {
    display: ${(props) => (props.$scrollable ? "block" : "none")};
  }
`;

const ColTeam = styled.div`
  color: ${(props) => props.color};
  font-weight: 600;
  font-size: 14px;
  opacity: 0.7;
  ${hideBelow(theme.breakpointsCustom.lg)}
`;

const ColPoints = styled.div`
  opacity: 0.7;
  font-size: 16px;
  text-align: right;
  ${hideBelow(theme.breakpointsCustom.lg)}
`;

const ColPointsDrops = styled.div`
  font-weight: 600;
  opacity: 1;
  display: block;
  font-size: 16px;
  text-align: right;
`;

const ColRaces = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  width: max-content;
  @media screen and (max-width: ${theme.breakpointsCustom.md}px) {
    display: ${(props) => (props.$scrollable ? "flex" : "none")};
  }
`;

const StyledRaceCell = styled.div`
  flex: 0 0 ${RACE_CELL}px;
  width: ${RACE_CELL}px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  border-radius: 2px;
  ${(props) => props.variant === "dropped" && `background: #ff000030;`}
  ${(props) => props.variant === "points" && `background: #3a3a3a;`}
  ${(props) => props.variant === "empty" && `background: rgba(255,255,255,0.02);`}
  &:hover {
    transform: translateY(-1px);
  }
`;

const RoundTooltip = styled.span`
  position: absolute;
  /* Below the flag, not above: the scroll container's overflow-x:auto forces overflow-y to auto
     as well, so anything rendered above the first row is clipped away. */
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background-color: ${theme.colors.dark};
  border: 1px solid ${theme.colors.grey}66;
  border-radius: 4px;
  padding: 4px 8px;
  white-space: nowrap;
  pointer-events: none;
  /* the header sets uppercase + letter-spacing; a track name should read normally */
  font-size: 11px;
  font-weight: 600;
  letter-spacing: normal;
  text-transform: none;
  color: ${theme.colors.white};
  opacity: 0;
  transition: opacity 0.15s ease-in-out;
  z-index: 5;
`;

const HeaderRaceCell = styled.div`
  position: relative;
  flex: 0 0 ${RACE_CELL}px;
  width: ${RACE_CELL}px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  /* hover covers desktop; the data attribute covers a tap on touch devices, which never hover */
  &:hover > ${RoundTooltip},
  &[data-open="true"] > ${RoundTooltip} {
    opacity: 1;
  }
`;

const HeaderFlag = styled.img`
  width: 20px;
  height: 14px;
  object-fit: cover;
  border-radius: 1px;
`;

const EmptyState = styled.div`
  padding: 24px 12px;
  color: ${theme.colors.grey};
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
`;

const RaceCell = ({ points, participated, dropped, raceId, seasonId, simulator }) => {
  let variant = "points";

  if (!participated) {
    variant = "empty";
  }

  if (dropped && participated) {
    variant = "dropped";
  }

  const params = new URLSearchParams({ view: "races", s: String(seasonId), r: String(raceId) });
  if (simulator) params.set("sim", simulator);

  return (
    <RaceLink to={`/results?${params.toString()}`}>
      <StyledRaceCell variant={variant}>{participated && points}</StyledRaceCell>
    </RaceLink>
  );
};

const SeasonTable = ({
  drivers,
  teams,
  seasonId,
  mode = "drivers",
  showHeader = false,
  scrollable = false,
}) => {
  const { seasons } = useScheduleContext();
  // Which round's tooltip a tap has opened. Touch devices never fire :hover, so the flag
  // needs an explicit open state; a tap anywhere else closes it again.
  const [openRound, setOpenRound] = useState(null);

  useEffect(() => {
    if (openRound === null) return undefined;
    const close = () => setOpenRound(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openRound]);

  const lastFinishedRaceIndex = _.findLastIndex(
    seasons[seasonId]?.races,
    (race) => race.hasResults,
  );

  const lastFinishedRaceId = seasons[seasonId]?.races[lastFinishedRaceIndex]?.id;

  const isTeams = mode === "teams";
  const rows = isTeams
    ? buildRowsFromTeamStandings(teams)
    : buildRowsFromStandings(drivers, { lastFinishedRaceId });

  if (!rows) {
    return <div>Loading season standings...</div>;
  }

  const season = seasons[seasonId];
  // Mirrors RaceSelector's tab detection, so a round cell lands on the right simulator tab
  const simulator =
    season?.seasonType === "SPECIAL_EVENT" ? "Special Events" : season?.simulator;

  // Drop rounds apply to drivers only (ADR-0008), and a season that drops none has a gross
  // column identical to its net column — printing the same number twice reads as a bug.
  const hasDrops = !isTeams && rows.some((row) => row.rounds.some((round) => round.dropped));
  const showTeamColumn = !isTeams;
  const roundsForHeader = rows.find((row) => row.rounds.length > 0)?.rounds || [];

  // Teams carry a roster line under the name, so they get a wider floor than drivers do.
  const nameCol = isTeams ? 260 : 200;
  const colsFull = gridTemplate({ team: showTeamColumn, gross: hasDrops, races: true, nameCol });
  const colsLg = gridTemplate({ team: false, gross: false, races: true, nameCol });
  const colsMd = gridTemplate({ team: false, gross: false, races: false, nameCol });
  const minWidth = rowWidth({
    team: showTeamColumn,
    gross: hasDrops,
    nameCol,
    roundCount: roundsForHeader.length,
  });
  const trackProps = {
    $colsFull: colsFull,
    $colsLg: colsLg,
    $colsMd: colsMd,
    $scrollable: scrollable,
    $minWidth: minWidth,
  };

  if (rows.length === 0) {
    return (
      <Root>
        <EmptyState>Залік ще не сформовано</EmptyState>
      </Root>
    );
  }

  return (
    <Root>
      <Table $scrollable={scrollable}>
        <TableBody>
          {showHeader && (
            <HeaderRow {...trackProps} as="div">
              <ColPosition>ПОЗ</ColPosition>
              <div>{isTeams ? "Команда" : "Пілот"}</div>
              {showTeamColumn && <ColTeam $scrollable={scrollable}>Команда</ColTeam>}
              {hasDrops && <ColPoints $scrollable={scrollable}>Всього</ColPoints>}
              <ColRaces $scrollable={scrollable}>
                {roundsForHeader.map((round, i) => (
                  <HeaderRaceCell
                    key={i}
                    data-open={openRound === i}
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpenRound(openRound === i ? null : i);
                    }}
                  >
                    {getFlag(round.flag) ? (
                      <HeaderFlag src={getFlag(round.flag)} alt={round.shortName || ""} />
                    ) : (
                      (round.shortName || "").slice(0, 3)
                    )}
                    {round.shortName && <RoundTooltip>{round.shortName}</RoundTooltip>}
                  </HeaderRaceCell>
                ))}
              </ColRaces>
              <ColPointsDrops>Оч.</ColPointsDrops>
            </HeaderRow>
          )}

          {rows.map((row) => (
            <Row key={row.id} color={row.color} {...trackProps}>
              <ColPosition>{row.position}</ColPosition>

              {isTeams ? (
                <TeamNameCell>
                  <div>{row.name}</div>
                  {row.drivers.length > 0 && <TeamRoster>{row.drivers.join(" / ")}</TeamRoster>}
                </TeamNameCell>
              ) : (
                <DriverName>
                  <DriverLink to={`/drivers/${row.id}`}>{row.name}</DriverLink>
                </DriverName>
              )}

              {showTeamColumn && (
                <ColTeam color={row.color} $scrollable={scrollable}>
                  {row.team}
                </ColTeam>
              )}

              {hasDrops && <ColPoints $scrollable={scrollable}>{row.pointsWithDrops}</ColPoints>}

              <ColRaces $scrollable={scrollable}>
                {row.rounds.map((round, i) => (
                  <RaceCell key={i} seasonId={seasonId} simulator={simulator} {...round} />
                ))}
              </ColRaces>

              <ColPointsDrops>{row.points}</ColPointsDrops>
            </Row>
          ))}
        </TableBody>
      </Table>
    </Root>
  );
};

export default SeasonTable;
